import { format as formatDateFns, isValid, parse } from "date-fns";
import { CalendarIcon } from "lucide-react";
import * as React from "react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

export interface DatePickerProps {
  /** Currently selected date, or `undefined` when empty. */
  value: Date | undefined;
  /** Called with the new date (or `undefined` when the field is cleared). */
  onChange: (date: Date | undefined) => void;
  /** Predicate for dates that cannot be selected/typed (matches `Calendar`'s `disabled`). */
  disabledDate?: (date: Date) => boolean;
  /** Placeholder shown in the text input when empty. */
  placeholder?: string;
  /** Format used to display and (primarily) parse the typed value. Defaults to `dd/MM/yyyy`. */
  dateFormat?: string;
  /** Disables the whole field (input + trigger). */
  disabled?: boolean;
  /** Popover alignment. */
  align?: "start" | "center" | "end";
  /** Caption layout forwarded to the underlying `Calendar`. */
  captionLayout?: React.ComponentProps<typeof Calendar>["captionLayout"];
  id?: string;
  className?: string;
  /**
   * Extra classes applied to the calendar `PopoverContent`. Useful for callers
   * that render the picker inside a high z-index overlay and need the popover
   * to stack above it (e.g. `"z-[10060]"`). Because `cn` uses `twMerge`, a
   * `z-*` class passed here overrides the default popover z-index.
   */
  popoverClassName?: string;
  "aria-invalid"?: boolean;
}

// Additional formats accepted when parsing typed input, so users can type a
// plain numeric date even when the field displays a more verbose format.
const FALLBACK_FORMATS = [
  "dd/MM/yyyy",
  "d/M/yyyy",
  "yyyy-MM-dd",
  "d MMM yyyy",
  "dd MMM yyyy",
  "PPP",
];

function tryParse(text: string, dateFormat: string): Date | undefined {
  const seen = new Set<string>();
  for (const fmt of [dateFormat, ...FALLBACK_FORMATS]) {
    if (seen.has(fmt)) continue;
    seen.add(fmt);
    const parsed = parse(text, fmt, new Date());
    // Require an exact round-trip so partial/garbage input is rejected.
    if (isValid(parsed) && formatDateFns(parsed, fmt) === text) {
      return parsed;
    }
  }
  return undefined;
}

export function DatePicker({
  value,
  onChange,
  disabledDate,
  placeholder = "dd/mm/yyyy",
  dateFormat = "dd/MM/yyyy",
  disabled,
  align = "start",
  captionLayout,
  id,
  className,
  popoverClassName,
  "aria-invalid": ariaInvalid,
}: DatePickerProps) {
  const [open, setOpen] = React.useState(false);
  const [focused, setFocused] = React.useState(false);
  const [text, setText] = React.useState(() =>
    value ? formatDateFns(value, dateFormat) : "",
  );

  // Keep the input text in sync with the external value, but never while the
  // user is actively typing (so we don't reformat mid-edit or move the cursor).
  React.useEffect(() => {
    if (!focused) {
      setText(value ? formatDateFns(value, dateFormat) : "");
    }
  }, [value, dateFormat, focused]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const next = event.target.value;
    setText(next);
    const trimmed = next.trim();
    if (trimmed === "") {
      onChange(undefined);
      return;
    }
    const parsed = tryParse(trimmed, dateFormat);
    if (parsed && !disabledDate?.(parsed)) {
      onChange(parsed);
    }
  };

  const handleSelect = (date: Date | undefined) => {
    onChange(date);
    setText(date ? formatDateFns(date, dateFormat) : "");
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverAnchor asChild>
        <div className={cn("relative", className)}>
          <Input
            id={id}
            value={text}
            placeholder={placeholder}
            disabled={disabled}
            aria-invalid={ariaInvalid}
            className="pr-9"
            onChange={handleInputChange}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
          />
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              disabled={disabled}
              tabIndex={-1}
              aria-label="Open calendar"
              className="absolute right-0 top-0 h-9 w-9 text-muted-foreground hover:bg-transparent"
            >
              <CalendarIcon className="h-4 w-4 opacity-60" />
            </Button>
          </PopoverTrigger>
        </div>
      </PopoverAnchor>
      <PopoverContent
        className={cn("z-[10050] w-auto overflow-hidden p-0", popoverClassName)}
        align={align}
      >
        <Calendar
          mode="single"
          selected={value}
          defaultMonth={value}
          captionLayout={captionLayout}
          disabled={disabledDate}
          onSelect={handleSelect}
        />
      </PopoverContent>
    </Popover>
  );
}
